import { createPortal } from "react-dom";
import { ImageOff } from "lucide-react";
import { ApiError } from "@/lib/api";

/** What the generation gateway accepts (JPEG, PNG, WebP, HEIC; 50 MB). */
export const IMAGE_ACCEPT = "image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif";
export const MAX_IMAGE_MB = 50;
const TYPES = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

export type UnsupportedReason = "type" | "size";

/** Checks a picked file before upload. Some browsers leave HEIC's type empty, so the extension counts too. */
export function checkImageFile(file: File): UnsupportedReason | null {
  const typeOk = TYPES.includes(file.type) || /\.(jpe?g|png|webp|heic|heif)$/i.test(file.name);
  if (!typeOk) return "type";
  if (file.size > MAX_IMAGE_MB * 1024 * 1024) return "size";
  return null;
}

/** A generate error for an image the gateway refused, as a popup reason. */
export function unsupportedReasonFromError(err: unknown): UnsupportedReason | null {
  const code = err instanceof ApiError ? err.code : undefined;
  if (code === "UNSUPPORTED_IMAGE") return "type";
  if (code === "IMAGE_TOO_LARGE") return "size";
  return null;
}

const COPY: Record<UnsupportedReason, { title: string; body: string }> = {
  type: { title: "We can't use that file", body: "Please upload a JPEG, PNG, WebP or HEIC image." },
  size: { title: "That image is too large", body: `Please upload an image that's ${MAX_IMAGE_MB} MB or smaller.` },
};

interface UnsupportedFileModalProps {
  reason: UnsupportedReason | null;
  onClose: () => void;
  /** Opens the file picker again. */
  onChooseAnother?: () => void;
}

export function UnsupportedFileModal({ reason, onClose, onChooseAnother }: UnsupportedFileModalProps) {
  if (!reason) return null;
  const { title, body } = COPY[reason];

  return createPortal(
    <div className="fixed inset-0 z-[9999]" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div className="fixed inset-0 bg-black/50 backdrop-blur-md" onClick={onClose} style={{ zIndex: 0 }} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="unsupported-file-title"
        className="relative rounded-3xl max-w-sm w-full mx-4 overflow-hidden shadow-2xl shadow-black/15 border border-white/40 backdrop-blur-xl animate-modal-in"
        style={{ zIndex: 1, background: "linear-gradient(170deg, #fff 0%, #faf6ed 100%)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative px-6 pt-7 pb-5 text-center">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[200px] h-[100px] bg-orange-500/8 rounded-full blur-[60px] pointer-events-none" />
          <div className="relative">
            <div className="size-14 rounded-2xl bg-orange-50 border border-orange-200/60 flex items-center justify-center mx-auto mb-3">
              <ImageOff className="size-7 text-[#e8822e]" />
            </div>
            <h3 id="unsupported-file-title" className="text-xl font-extrabold text-foreground mb-1">{title}</h3>
            <p className="text-[13px] text-muted-foreground leading-relaxed">{body}</p>
          </div>
        </div>

        <div className="px-5 pb-6 flex flex-col gap-2.5">
          {onChooseAnother && (
            <button
              onClick={() => { onClose(); onChooseAnother(); }}
              className="w-full py-3.5 rounded-2xl bg-gradient-to-r from-[#f0923a] to-[#e8822e] text-white text-[15px] font-extrabold shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/30 hover:-translate-y-px transition-all flex items-center justify-center gap-2"
            >
              Choose another image
            </button>
          )}
          <button
            onClick={onClose}
            className="w-full py-2 rounded-xl text-[12.5px] font-semibold text-muted-foreground hover:text-foreground transition-colors"
          >
            {onChooseAnother ? "Cancel" : "Got it"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
